import { CLASS_NAME } from 'constants/class-names'
import {
    EventType,
    HtmlAttr,
    KeyType,
    SlidingDrawerEvent
} from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'

export enum SlidingDrawerAnimations {
    OPEN = 'drawerSlideUp',
    CLOSE = 'drawerSlideDown'
}

export class SlidingDrawerComponent extends ComponentBase {
    private drawerClose: Nullable<HTMLButtonElement>
    private consentBanner: Nullable<HTMLElement>

    /**
     * Get backdrop element if option provided from Django template
     * @return {Nullable<HTMLElement>}
     */
    private get backdropEl(): Nullable<HTMLElement> {
        return this.getElement(`.${CLASS_NAME.slidingDrawer.backdropEl}`)
    }

    constructor(private drawer: HTMLElement) {
        super()

        this.drawerClose = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.slidingDrawer.close}`,
            drawer
        )
        this.consentBanner = this.getElement(
            `.${CLASS_NAME.slidingDrawer.consentBannerPairing}`
        )

        window.addEventListener(SlidingDrawerEvent.OPEN, (ev: CustomEvent) =>
            this.openDrawer(ev.detail)
        )
        window.addEventListener(SlidingDrawerEvent.CLOSE, (ev: CustomEvent) =>
            this.closeDrawer(ev.detail)
        )

        // watch for completion of animation that opens and closes the drawer
        // dispatch event for their completion
        document.addEventListener(
            EventType.ANIMATION_END,
            (ev: AnimationEvent) => this.animationEndEventsHandler(ev)
        )

        // close drawer on backdrop click
        if (this.backdropEl) {
            this.backdropEl.addEventListener(EventType.CLICK, () => {
                if (this.backdropEl && this.backdropEl.dataset) {
                    this.closeDrawer(this.backdropEl.dataset.backdropSibling)
                }
                this.enableScroll()
            })
        }

        if (this.drawerClose) {
            this.drawerClose.addEventListener(EventType.CLICK, () => {
                this.closeDrawer(this.drawer.id)
            })

            // when dismiss-able is allowed a close button should exist
            // attach escape key exit then
            document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
                if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
                    if (
                        this.drawer &&
                        this.drawer.classList.contains(
                            CLASS_NAME.slidingDrawer.slideUp
                        )
                    ) {
                        this.closeDrawer(this.drawer.id)
                        this.enableScroll()
                    }
                }
            })
        }
    }

    private openDrawer(drawerId: string): void {
        if (this.drawer && this.drawer.id !== drawerId) {
            return
        }
        // if GDPR is present - animate if bottom position only
        // data-sliding-drawer='bottom'
        if (
            this.drawer &&
            this.drawer.dataset.slidingDrawerPosition === 'bottom' &&
            this.consentBanner &&
            !this.consentBanner.hidden
        ) {
            this.consentBanner.style.transform = `translateY(-${this.drawer.clientHeight}px)`
        }

        this.drawer.classList.remove(CLASS_NAME.slidingDrawer.slideDown)
        this.drawer.classList.add(CLASS_NAME.slidingDrawer.slideUp)
        this.drawer.removeAttribute(HtmlAttr.TAB_INDEX)
        this.disableScroll()
    }

    private closeDrawer(drawerId = ''): void {
        if (this.drawer && this.drawer.id !== drawerId) {
            return
        }
        if (this.consentBanner && !this.consentBanner.hidden) {
            this.consentBanner.style.transform = 'translateY(0)'
        }
        this.closeAll()
        this.enableScroll()
    }

    private closeAll(): void {
        this.drawer.classList.remove(CLASS_NAME.slidingDrawer.slideUp)
        this.drawer.classList.add(CLASS_NAME.slidingDrawer.slideDown)
        this.drawer.tabIndex = -1
    }

    private enableScroll(): void {
        if (this.drawer.dataset.backdrop && this.backdropEl) {
            document.body.classList.remove(
                CLASS_NAME.slidingDrawer.backdropSupport
            )
        }
    }

    private disableScroll(): void {
        if (this.drawer.dataset.backdrop && this.backdropEl) {
            document.body.classList.add(
                CLASS_NAME.slidingDrawer.backdropSupport
            )
        }
    }

    private animationEndEventsHandler(ev: AnimationEvent): void {
        if (ev.animationName === SlidingDrawerAnimations.CLOSE) {
            window.dispatchEvent(new CustomEvent(SlidingDrawerEvent.CLOSED))
        }
        if (ev.animationName === SlidingDrawerAnimations.OPEN) {
            window.dispatchEvent(new CustomEvent(SlidingDrawerEvent.OPENED))
        }
    }
}
